/**
 * Audit action taxonomy — the ONE place an AuditAction becomes something a human reads.
 *
 * Every value of the Prisma `AuditAction` enum appears here exactly once, with:
 *   label    — what the Activity Log shows in the Action column
 *   category — the group it sits under in the two-level action filter
 *   tone     — the colour of the leading dot (semantic, not decorative)
 *
 * WHY THIS IS EXHAUSTIVE, AND ENFORCED:
 * The page used to carry an 8-entry label map against a 67-value enum, so anything specific
 * rendered raw — a barrister saw `conflict_override_approved` and `mailbox_sync_failed` in
 * snake_case. `AUDIT_ACTION_META` is typed as Record<AuditActionValue, …>, so adding an enum
 * value without a label is a COMPILE error, and audit-actions.conformance.test.ts fails if the
 * Prisma enum, the API's AUDIT_ACTIONS and this table ever drift apart.
 *
 * The API cannot import this file (its tsconfig sets rootDir: src — same constraint as
 * settings-registry and the upload contract), so the action list exists twice and the
 * conformance test is what keeps the copies honest.
 */

/** Filter categories, in the order the picker shows them. */
export const AUDIT_CATEGORIES = [
  'matters',
  'fees',
  'documents',
  'communication',
  'conflicts',
  'people',
  'auth',
  'system',
  'support',
] as const;

export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

export const AUDIT_CATEGORY_LABEL: Record<AuditCategory, string> = {
  matters:       'Matters & records',
  fees:          'Fees & payments',
  documents:     'Documents',
  communication: 'Email & communication',
  conflicts:     'Conflicts',
  people:        'People & access',
  auth:          'Sign-in & security',
  system:        'System',
  support:       'Support',
};

/** Dot colour. Semantic — what KIND of thing happened, never per-category decoration. */
export type AuditTone = 'created' | 'changed' | 'removed' | 'viewed' | 'money' | 'auth' | 'alert';

export interface AuditActionMeta {
  label: string;
  category: AuditCategory;
  tone: AuditTone;
}

/**
 * Every AuditAction, exactly once.
 *
 * The generic CRUD four are deliberately in `matters`: they are overwhelmingly emitted against
 * Case/Hearing/Task/Contact rows, and they are the only actions a non-admin can see at all
 * (audit-log.service.ts restricts non-admins to OPERATIONAL_ACTIONS). Putting them anywhere
 * else would leave a clerk's category picker empty.
 */
export const AUDIT_ACTION_META = {
  // ─── Generic CRUD ────────────────────────────────────────────
  create:                     { label: 'Created',                   category: 'matters',       tone: 'created' },
  read:                       { label: 'Viewed',                    category: 'matters',       tone: 'viewed'  },
  update:                     { label: 'Updated',                   category: 'matters',       tone: 'changed' },
  delete:                     { label: 'Deleted',                   category: 'matters',       tone: 'removed' },
  hearing_outcome_recorded:   { label: 'Hearing outcome recorded',  category: 'matters',       tone: 'created' },
  hearing_outcome_amended:    { label: 'Hearing outcome amended',   category: 'matters',       tone: 'changed' },
  export:                     { label: 'Exported',                  category: 'documents',     tone: 'viewed'  },
  download:                   { label: 'Downloaded',                category: 'documents',     tone: 'viewed'  },
  share:                      { label: 'Shared',                    category: 'documents',     tone: 'changed' },

  // ─── Sign-in & security ──────────────────────────────────────
  login:                      { label: 'Signed in',                 category: 'auth',          tone: 'auth'    },
  logout:                     { label: 'Signed out',                category: 'auth',          tone: 'auth'    },
  failed_login:               { label: 'Failed sign-in',            category: 'auth',          tone: 'alert'   },
  permission_denied:          { label: 'Permission denied',         category: 'auth',          tone: 'alert'   },
  password_changed:           { label: 'Password changed',          category: 'auth',          tone: 'changed' },
  password_reset_requested:   { label: 'Password reset requested',  category: 'auth',          tone: 'auth'    },
  password_reset_completed:   { label: 'Password reset completed',  category: 'auth',          tone: 'changed' },
  account_locked:             { label: 'Account locked',            category: 'auth',          tone: 'alert'   },
  account_unlocked:           { label: 'Account unlocked',          category: 'auth',          tone: 'changed' },
  mfa_enabled:                { label: 'Two-factor enabled',        category: 'auth',          tone: 'created' },
  mfa_disabled:               { label: 'Two-factor disabled',       category: 'auth',          tone: 'alert'   },
  mfa_backup_used:            { label: 'Backup code used',          category: 'auth',          tone: 'alert'   },
  mfa_reset:                  { label: 'Two-factor reset',          category: 'auth',          tone: 'alert'   },
  session_created:            { label: 'Session started',           category: 'auth',          tone: 'auth'    },
  session_revoked:            { label: 'Session revoked',           category: 'auth',          tone: 'removed' },
  all_sessions_revoked:       { label: 'All sessions revoked',      category: 'auth',          tone: 'alert'   },
  portal_login:               { label: 'Solicitor portal sign-in',  category: 'auth',          tone: 'auth'    },
  portal_session_created:     { label: 'Portal session started',    category: 'auth',          tone: 'auth'    },
  magic_link_sent:            { label: 'Sign-in link sent',         category: 'auth',          tone: 'auth'    },
  magic_link_used:            { label: 'Sign-in link used',         category: 'auth',          tone: 'auth'    },

  // ─── People & access ─────────────────────────────────────────
  role_changed:               { label: 'Role changed',              category: 'people',        tone: 'changed' },
  invite_sent:                { label: 'Invitation sent',           category: 'people',        tone: 'created' },
  invite_accepted:            { label: 'Invitation accepted',       category: 'people',        tone: 'created' },
  invite_cancelled:           { label: 'Invitation cancelled',      category: 'people',        tone: 'removed' },

  // ─── Documents ───────────────────────────────────────────────
  document_viewed:            { label: 'Document viewed',           category: 'documents',     tone: 'viewed'  },
  document_version_created:   { label: 'New version uploaded',      category: 'documents',     tone: 'created' },
  document_version_restored:  { label: 'Version restored',          category: 'documents',     tone: 'changed' },
  legal_hold_applied:         { label: 'Legal hold applied',        category: 'documents',     tone: 'alert'   },
  legal_hold_released:        { label: 'Legal hold released',       category: 'documents',     tone: 'changed' },

  // ─── Fees & payments ─────────────────────────────────────────
  fee_note_created:           { label: 'Fee note drafted',          category: 'fees',          tone: 'created' },
  fee_note_sent:              { label: 'Fee note sent',             category: 'fees',          tone: 'money'   },
  fee_note_paid:              { label: 'Fee note paid',             category: 'fees',          tone: 'money'   },
  fee_note_voided:            { label: 'Fee note voided',           category: 'fees',          tone: 'removed' },
  fee_note_written_off:       { label: 'Fee note written off',      category: 'fees',          tone: 'alert'   },
  payment_recorded:           { label: 'Payment recorded',          category: 'fees',          tone: 'money'   },
  payment_voided:             { label: 'Payment voided',            category: 'fees',          tone: 'removed' },
  credit_note_created:        { label: 'Credit note issued',        category: 'fees',          tone: 'money'   },
  credit_note_voided:         { label: 'Credit note voided',        category: 'fees',          tone: 'removed' },

  // ─── Conflicts (BSB) ─────────────────────────────────────────
  conflict_scanned:           { label: 'Conflict check run',        category: 'conflicts',     tone: 'viewed'  },
  conflict_rechecked:         { label: 'Conflict re-checked',       category: 'conflicts',     tone: 'viewed'  },
  conflict_override_approved: { label: 'Conflict override approved', category: 'conflicts',    tone: 'alert'   },
  conflict_review_rejected:   { label: 'Conflict review rejected',  category: 'conflicts',     tone: 'alert'   },

  // ─── Email & communication ───────────────────────────────────
  email_sent:                 { label: 'Email sent',                category: 'communication', tone: 'created' },
  email_delivered:            { label: 'Email delivered',           category: 'communication', tone: 'created' },
  email_bounced:              { label: 'Email bounced',             category: 'communication', tone: 'alert'   },
  email_received:             { label: 'Email received',            category: 'communication', tone: 'created' },
  email_complained:           { label: 'Spam complaint',            category: 'communication', tone: 'alert'   },
  email_imported:             { label: 'Email imported',            category: 'communication', tone: 'created' },
  email_linked:               { label: 'Email linked to matter',    category: 'communication', tone: 'changed' },
  email_unlinked:             { label: 'Email unlinked',            category: 'communication', tone: 'removed' },
  attachment_imported:        { label: 'Attachment imported',       category: 'communication', tone: 'created' },
  attachment_linked:          { label: 'Attachment linked',         category: 'communication', tone: 'changed' },
  draft_created:              { label: 'Draft saved',               category: 'communication', tone: 'created' },
  draft_deleted:              { label: 'Draft deleted',             category: 'communication', tone: 'removed' },
  follow_up_created:          { label: 'Follow-up set',             category: 'communication', tone: 'created' },

  // ─── System ──────────────────────────────────────────────────
  mailbox_connected:          { label: 'Mailbox connected',         category: 'system',        tone: 'created' },
  mailbox_disconnected:       { label: 'Mailbox disconnected',      category: 'system',        tone: 'removed' },
  mailbox_sync_started:       { label: 'Mailbox sync started',      category: 'system',        tone: 'viewed'  },
  mailbox_sync_completed:     { label: 'Mailbox sync completed',    category: 'system',        tone: 'created' },
  mailbox_sync_failed:        { label: 'Mailbox sync failed',       category: 'system',        tone: 'alert'   },

  // ─── Support ─────────────────────────────────────────────────
  // The FACT that a ticket exists is auditable; its title and body are not written here.
  // Ticket visibility is requester-only, so a chambers admin reading this log learns that a
  // member raised a ticket, never what it says. See platform-support: the metadata is ids only.
  support_ticket_created:     { label: 'Support ticket raised',     category: 'support',       tone: 'created' },
  support_ticket_replied:     { label: 'Support ticket reply sent', category: 'support',       tone: 'changed' },
} as const satisfies Record<string, AuditActionMeta>;

export type AuditActionValue = keyof typeof AUDIT_ACTION_META;

/** Every action, in enum order — the flat list behind "All actions". */
export const AUDIT_ACTION_VALUES = Object.keys(AUDIT_ACTION_META) as AuditActionValue[];

/** The actions in one category, for the second select in the two-level filter. */
export function auditActionsInCategory(category: AuditCategory): AuditActionValue[] {
  return AUDIT_ACTION_VALUES.filter((a) => AUDIT_ACTION_META[a].category === category);
}

/**
 * Human label for an action. Falls back to de-snake-casing rather than rendering raw, so a
 * value added to the enum before this table still reads as English on the page.
 */
export function auditActionLabel(action: string): string {
  const meta = (AUDIT_ACTION_META as Record<string, AuditActionMeta | undefined>)[action];
  if (meta) return meta.label;
  return action.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export function auditActionTone(action: string): AuditTone {
  return (AUDIT_ACTION_META as Record<string, AuditActionMeta | undefined>)[action]?.tone ?? 'changed';
}
